import React, { useEffect, useState } from "react";

// Define the styles for different variants with a modern, polished look
// Using Tailwind CSS for styling
const variants = {
  success: {
    container: "bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500",
    iconBg: "bg-green-500",
    iconColor: "text-white",
    closeBtnColor: "text-green-700 hover:text-green-800 dark:text-green-300",
    btnColor: "bg-green-600 hover:bg-green-700 text-white shadow-sm",
    iconPath: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
  },
  error: {
    container: "bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500",
    iconBg: "bg-red-500",
    iconColor: "text-white",
    closeBtnColor: "text-red-700 hover:text-red-800 dark:text-red-300",
    btnColor: "bg-red-600 hover:bg-red-700 text-white shadow-sm",
    iconPath: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
  },
  warning: {
    container: "bg-amber-50 dark:bg-amber-900/30 border-l-4 border-amber-500",
    iconBg: "bg-amber-500",
    iconColor: "text-white",
    closeBtnColor: "text-amber-800 hover:text-amber-900 dark:text-amber-300",
    btnColor: "bg-amber-600 hover:bg-amber-700 text-white shadow-sm",
    iconPath: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    ),
  },
  info: {
    container: "bg-blue-50 dark:bg-blue-900/30 border-blue-500",
    iconBg: "bg-blue-500",
    iconColor: "text-white",
    closeBtnColor: "text-blue-700 hover:text-blue-800 dark:text-blue-300",
    btnColor: "bg-blue-600 hover:bg-blue-700 text-white shadow-sm",
    iconPath: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
  },
};

export type ToastType = keyof typeof variants;

export interface ToastProps {
  /** Unqiue ID for the toast, useful for lists */
  id?: string | number;
  /** The type of the toast variant */
  type?: ToastType;
  /** Alias for type */
  variant?: ToastType;
  /** Title of the toast */
  title?: string;
  /** Main content of the toast */
  message: React.ReactNode;
  /** Optional custom icon override */
  icon?: React.ReactNode;
  /** Callback when the toast closes */
  onClose?: () => void;
  /** Duration in ms. Defaults to 0 (no auto-close). */
  duration?: number;
  /** Optional action button configuration */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Additional custom classes */
  className?: string;
}

const Alert: React.FC<ToastProps> = ({
  type = "info",
  variant,
  title,
  message,
  icon,
  onClose,
  duration = 0,
  action,
  className = "",
}) => {
  const activeVariant = variant || type;
  // Internal state to manage entry/exit animations
  const [isVisible, setIsVisible] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    // Trigger entry animation
    const raf = requestAnimationFrame(() => {
      setIsVisible(true);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    // Wait for the exit transition to complete before triggering onClose
    setTimeout(() => {
      setRemoved(true);
      if (onClose) onClose();
    }, 400); // 400ms matches the duration-300 + buffer
  };

  if (removed) return null;

  const style = variants[activeVariant];

  return (
    <div
      className={`
        relative w-full  
        transform transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
        ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}
        ${style.container}
        ${className}
      `}
      role="alert"
    >
      <div className="p-4 flex items-center w-full">
        {/* Icon Circle */}
        <div className="flex-shrink-0">
          <div
            className={`flex items-center justify-center h-8 w-8 rounded-full ${style.iconBg}`}
          >
            {icon ? (
              <div className={`h-5 w-5 ${style.iconColor}`}>{icon}</div>
            ) : (
              <svg
                className={`h-5 w-5 ${style.iconColor}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                {style.iconPath}
              </svg>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="ml-4 flex-1 w-full">
          {title && (
            <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight mb-1">
              {title}
            </h3>
          )}
          <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed ">
            {message}
          </div>
        </div>

        {/* Actions & Close */}
        <div className="ml-6 flex items-center gap-4">
          {action && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className={`
                px-4 py-1.5 rounded-full text-xs font-semibold shadow-sm
                transition-transform duration-200 active:scale-95
                focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-gray-800
                ${style.btnColor}
              `}
            >
              {action.label}
            </button>
          )}

          <button
            type="button"
            className={`
              inline-flex rounded-md bg-transparent p-1.5 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800
              ${style.closeBtnColor}
            `}
            onClick={handleClose}
          >
            <span className="sr-only">Close</span>
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Alert;